import { format, parseISO } from 'date-fns';

/**
 * One recorded row of segment_analytics_daily, as GET /analytics/segments/:siteId/timeseries
 * returns it (backend-merged/routes/analytics-segments.js). A metric is `null` when the row did
 * not record it; it is never invented here.
 */
export interface DailySeriesPoint {
  date: string; // ISO yyyy-mm-dd
  segmentSlug: string;
  segmentName: string;
  metrics: {
    totalUsers: number | null;
    conversionRate: number | null;
    avgOrderValue: number | null;
    totalRevenue: number | null;
    lifetimeValue: number | null;
    avgSessionDuration: number | null;
    bounceRate: number | null;
    repeatPurchaseRate: number | null;
  };
}

export type DailyMetricKey = keyof DailySeriesPoint['metrics'];

export interface DailySeriesResponse {
  source: 'segment_analytics_daily';
  periodDays: number;
  rowCount: number;
  series: DailySeriesPoint[];
}

/** Recharts row: one per recorded date, one key per requested segment slug that has a value that day. */
export type PivotedRow = { date: string; iso: string } & Record<string, string | number>;

/**
 * Pivot recorded daily rows into recharts rows for the given segments and metric.
 *
 * - Only dates that exist in `series` appear. Nothing is interpolated or padded.
 * - A segment with no row on a date, or a null metric that day, gets NO key for that date, so the
 *   chart shows a gap rather than a made-up point. The date row itself is kept whenever any wanted
 *   segment recorded that day, so the gap is visible instead of the date vanishing.
 * - Slugs not present in `slugs` are ignored (the page charts its top three).
 * - Output is ordered by ISO date ascending regardless of input order.
 *
 * task-s830jr3i34: this replaces getTimeSeriesData / getUserGrowthData, which jittered the latest
 * aggregate with Math.random() to draw a curve that was never measured.
 */
export function pivotDailySeries(
  series: DailySeriesPoint[],
  slugs: string[],
  metric: DailyMetricKey,
): PivotedRow[] {
  const wanted = new Set(slugs);
  const byDate = new Map<string, PivotedRow>();
  for (const point of series) {
    if (!wanted.has(point.segmentSlug)) continue;
    // The DATE row exists because a wanted segment recorded that day, even if this metric was
    // not measured: otherwise a null between two values would drop the date and recharts would
    // connect its neighbours, hiding the gap the null represents (codex on #6313).
    let row = byDate.get(point.date);
    if (!row) {
      row = { iso: point.date, date: format(parseISO(point.date), 'MMM dd') } as PivotedRow;
      byDate.set(point.date, row);
    }
    const value = point.metrics[metric];
    if (value === null || value === undefined || Number.isNaN(value)) continue;
    row[point.segmentSlug] = value;
  }
  return [...byDate.values()].sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));
}

/** The page's metric selector values map 1:1 onto recorded daily columns; anything else is not chartable. */
export function toDailyMetricKey(selectedMetric: string): DailyMetricKey | null {
  const keys: DailyMetricKey[] = [
    'totalUsers', 'conversionRate', 'avgOrderValue', 'totalRevenue',
    'lifetimeValue', 'avgSessionDuration', 'bounceRate', 'repeatPurchaseRate',
  ];
  return (keys as string[]).includes(selectedMetric) ? (selectedMetric as DailyMetricKey) : null;
}
